import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FundingSubmissionStatusBadge from './FundingSubmissionStatusBadge';
import FundingSubmissionDocumentChecklist from './FundingSubmissionDocumentChecklist';
import FundingSubmissionTimeline from './FundingSubmissionTimeline';
import { Button } from '../ui/button';
import { useFundingRoutes } from '../../hooks/funding/useFundingRoutes';

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const DetailRow = ({ label, value }) => (
  <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-3 py-2.5">
    <div className="text-[11px] font-semibold uppercase tracking-wide text-[var(--fg-muted)]">{label}</div>
    <div className="mt-1 text-sm text-[var(--fg-text)]">{value || '—'}</div>
  </div>
);

const FundingSubmissionDetail = ({ submission, onClose, onCreateDeal, creatingDeal, dealCreated }) => {
  const navigate = useNavigate();
  const { pipeline: pipelinePath, lenders: lendersPath } = useFundingRoutes();

  if (!submission) return null;

  const showDealPrompt = ['Approved', 'Funded'].includes(submission.status);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          {submission.lenderId ? (
            <Link to={`${lendersPath}/${submission.lenderId}`} className="text-lg font-semibold text-[var(--fg-accent)] hover:underline">
              {submission.lenderName}
            </Link>
          ) : (
            <div className="text-lg font-semibold text-[var(--fg-text)]">{submission.lenderName}</div>
          )}
          <div className="text-sm text-[var(--fg-muted)]">{submission.clientName} · {submission.productType}</div>
        </div>
        <FundingSubmissionStatusBadge status={submission.status} />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <DetailRow label="Requested amount" value={formatCurrency(submission.requestedAmount)} />
        <DetailRow label="Submitted amount" value={formatCurrency(submission.submittedAmount)} />
        <DetailRow label="Approved amount" value={formatCurrency(submission.approvedAmount)} />
        <DetailRow label="Funded amount" value={formatCurrency(submission.fundedAmount)} />
        <DetailRow label="Submission method" value={submission.submissionMethod} />
        <DetailRow label="Documents status" value={submission.documentsStatus} />
        <DetailRow label="Signature status" value={submission.signatureStatus} />
        <DetailRow label="Next follow-up" value={submission.nextFollowUpAt} />
      </div>

      <FundingSubmissionTimeline submission={submission} />
      <FundingSubmissionDocumentChecklist submission={submission} />

      {submission.lenderResponse ? (
        <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-3 py-2 text-sm">
          <span className="font-semibold text-[var(--fg-accent)]">Lender response: </span>
          {submission.lenderResponse}
        </div>
      ) : null}

      {submission.declineReason ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/30 dark:text-rose-200">
          <span className="font-semibold">Decline reason: </span>
          {submission.declineReason}
        </div>
      ) : null}

      {submission.notes ? <DetailRow label="Notes" value={submission.notes} /> : null}

      {(submission.matchId || submission.sequenceItemId) ? (
        <div className="text-xs text-[var(--fg-muted)]">
          Linked match: {submission.matchId || '—'} · Linked sequence item: {submission.sequenceItemId || '—'}
        </div>
      ) : null}

      {showDealPrompt ? (
        <div className="rounded-xl border border-[var(--fg-line)] bg-[var(--fg-panel-soft)] px-4 py-3">
          <div className="text-sm font-medium text-[var(--fg-text)]">
            {submission.status === 'Funded' ? 'Funded submission ready for pipeline tracking' : 'Approved submission ready for deal pipeline'}
          </div>
          <p className="mt-1 text-xs text-[var(--fg-muted)]">
            Create or update a deal to track projected commissions and next tasks.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              className="rounded-xl bg-[var(--fg-accent)] hover:bg-[#126b4f]"
              disabled={creatingDeal}
              onClick={() => onCreateDeal?.(submission)}
            >
              {creatingDeal ? 'Creating...' : 'Create Deal'}
            </Button>
            {dealCreated ? (
              <Button size="sm" variant="outline" className="rounded-xl" onClick={() => navigate(pipelinePath)}>
                Open Pipeline
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {onClose ? (
        <button type="button" onClick={onClose} className="rounded-xl border border-[var(--fg-line)] px-4 py-2 text-sm text-[var(--fg-muted)]">
          Close
        </button>
      ) : null}
    </div>
  );
};

export default FundingSubmissionDetail;
